import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { showToast } from "../../../../core/utils/alert";
import { CourseFeeSharePayloadType } from "../types/courseFeeShareRequest";
import { useCourseFeeShare } from "../hooks/useCourseFeeShare";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { useFeeType } from "../../../feetypes/hooks/useFeeType";
import { useCenter } from "../../../peoples/centers/hooks/useCenter";
import { useFeeConfig } from "../../../feeconfig/courseFeeConfig/hooks/useFeeConfig";
import CommonSelect from "../../../../core/common/commonSelect";
import MultiSelect, { Option } from "../../../../core/common/MultiSelect";

const AddCourseFeeShare = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Determine if we're in edit mode
  const [isEdit, setIsEdit] = useState<boolean>(!!id);

  // Use the hooks to fetch data for dropdowns
  const { getCourseState } = useCourse();
  const { getFeeTypeState } = useFeeType();
  const { useCenterList } = useCenter({ skip: true });
  const {
    addFeeShare,
    updateFeeShare,
    isAdding,
    isUpdating,
    useGetFeeShareById,
  } = useCourseFeeShare();
  const { useGetFeeConfigById } = useFeeConfig();

  // Get fee share data if in edit mode
  const { feeShare, loading: isLoadingFeeShare } = useGetFeeShareById(id || "");

  // Get data from hooks
  const { courses, isLoading: isLoadingCourses } = getCourseState();
  const { feetypes, isLoading: isLoadingFeeTypes } = getFeeTypeState();
  const { data: centerResponse, isLoading: isLoadingCenters } = useCenterList();
  const centers = centerResponse?.data?.items || [];

  // State to store fee config IDs
  const [courseFeeConfigId, setCourseFeeConfigId] = useState<string>("");

  // Get fee config data
  const { feeConfig: courseFeeConfig, loading: isLoadingCourseFeeConfig } =
    useGetFeeConfigById(courseFeeConfigId);

  // State to store course stages
  const [courseStages, setCourseStages] = useState<
    {
      id?: number;
      stage_id: number;
      name: string;
      amount: number;
      shareAmount: number;
    }[]
  >([]);

  // Transform data for select components
  const courseOptions = courses.map((course) => ({
    value: course.id.toString(),
    label: course.name,
  }));

  // Get the first course fee type from feetypes
  const courseFeeType = feetypes.find(
    (feetype) => feetype.type === "coursefee"
  );

  // Fee type option
  const feeTypeOption = courseFeeType
    ? { value: courseFeeType.id.toString(), label: "Course Fee" }
    : null;

  const centerOptions = useMemo(
    () =>
      centers.map((center) => ({
        value: center.id.toString(),
        label: center.name,
      })),
    [centers]
  );

  const shareTypeOptions = [
    { value: "fixed", label: "Fixed Amount (₹)" },
    { value: "percent", label: "Percentage (%)" },
  ];

  // Initialize with empty values and set type_id to courseFeeType if available
  const [formData, setFormData] = useState<CourseFeeSharePayloadType>({
    course_id: 0,
    type_id: courseFeeType ? courseFeeType.id : 0,
    share_type: "",
    all_centers: false,
    center_ids: [],
    stageFeeShare: [],
  });

  const [selectedCenters, setSelectedCenters] = useState<Option[]>([]);
  const [selectedCourse, setSelectedCourse] = useState("");

  // State to track filtered centers
  const [filteredCenterOptions, setFilteredCenterOptions] = useState<Option[]>(
    []
  );

  // Special effect for edit mode to ensure fee config is loaded early
  useEffect(() => {
    if (isEdit && feeShare && feeShare.course_id) {
      console.log(
        "Edit mode: Setting course fee config ID early:",
        feeShare.course_id.toString()
      );
      setCourseFeeConfigId(feeShare.course_id.toString());
    }
  }, [isEdit, feeShare]);

  // Effect to populate form data when fee share is loaded in edit mode
  useEffect(() => {
    if (isEdit && feeShare) {
      console.log("Loaded fee share data:", feeShare);

      // Set form data from the fee share object
      setFormData({
        course_id: feeShare.course?.id || 0,
        type_id: feeShare.feeType?.id || 0,
        share_type: feeShare.share_type || "",
        all_centers: feeShare.all_centers || false,
        center_ids:
          feeShare.centers?.map((center: { id: number }) => center.id) || [],
        stageFeeShare:
          feeShare.stageFeeShare?.map(
            (stage: {
              id: number;
              stage_id: number;
              share_amount: number;
            }) => ({
              id: stage.id,
              stage_id: stage.stage_id,
              share_amount: stage.share_amount,
            })
          ) || [],
      });

      // Set selected course
      setSelectedCourse(feeShare.course?.id?.toString() || "");

      // Set course fee config ID to trigger loading of fee config
      if (feeShare.course?.id) {
        setCourseFeeConfigId(feeShare.course.id.toString());
      }

      // Set selected centers
      if (!feeShare.all_centers && feeShare.centers?.length > 0) {
        const selectedCenterOptions = feeShare.centers.map(
          (center: { id: number; name: string }) => ({
            value: center.id.toString(),
            label: center.name,
          })
        );
        setSelectedCenters(selectedCenterOptions);
      }
    }
  }, [isEdit, feeShare]);

  // Effect to set course fee type when component loads
  useEffect(() => {
    if (courseFeeType && formData.type_id === 0) {
      setFormData((prev) => ({
        ...prev,
        type_id: courseFeeType.id,
      }));
    }
  }, [feetypes, courseFeeType]);

  // Effect to fetch fee configs when course are selected
  useEffect(() => {
    // console.log("Checking for course fee config:", {
    //   course_id: formData.course_id,
    //   hasCourseFeeType: !!courseFeeType,
    // });

    if (formData.course_id && courseFeeType) {
      // console.log(
      //   "Setting course fee config ID:",
      //   formData.course_id.toString()
      // );
      // Set course fee config ID (using course_id for now)
      setCourseFeeConfigId(formData.course_id.toString());
    } else {
      setCourseFeeConfigId("");
    }
  }, [formData.course_id, courseFeeType]);

  // Effect to update course stages when course fee config is loaded
  useEffect(() => {
    if (courseFeeConfig?.coursefee && courseFeeConfig.coursefee.stageFees) {
      const stages = courseFeeConfig.coursefee.stageFees.map((stage) => {
        // Find the corresponding course stage to get its name
        const selectedCourse = courses.find(
          (course) => course.id === formData.course_id
        );

        // Find the stage in the course's stages
        const courseStage = selectedCourse?.courseStages?.find(
          (cs) => cs.id === stage.stage_id
        );

        // Use the actual stage name from the course data, or fallback to a generic name
        const stageName = courseStage?.name || `Stage ${stage.stage_id}`;

        // Find share amount from existing data if available
        const existingStageShare = formData.stageFeeShare.find(
          (s) => s.stage_id === stage.stage_id
        );

        return {
          id: existingStageShare?.id,
          stage_id: stage.stage_id,
          name: stageName,
          amount: stage.amount,
          shareAmount: existingStageShare?.share_amount || 0,
        };
      });

      setCourseStages(stages);
    } else {
      setCourseStages([]);
    }
  }, [courseFeeConfig, courses, formData.course_id, formData.stageFeeShare]);

  // Effect to filter centers based on selected course
  useEffect(() => {
    if (formData.course_id && courseFeeConfig?.coursefee) {
      // Filter centers for course fee
      const attachedCenterIds =
        courseFeeConfig.coursefee.atachedToCenters?.map(
          (center) => center.id
        ) || [];

      const filtered = centers
        .filter((center) => attachedCenterIds.includes(center.id))
        .map((center) => ({
          value: center.id.toString(),
          label: center.name,
        }));

      setFilteredCenterOptions(filtered);

      // Reset selected centers if they're not in the filtered list
      setSelectedCenters((prev) =>
        prev.filter((selected) =>
          filtered.some((option) => option.value === selected.value)
        )
      );

      // If no centers are available, uncheck "Apply to All Centers"
      if (filtered.length === 0 && formData.all_centers) {
        setFormData((prev) => ({
          ...prev,
          all_centers: false,
          center_ids: prev.center_ids?.filter((id) =>
            attachedCenterIds.includes(id)
          ),
        }));
      } else {
        // Just update center_ids filtering
        setFormData((prev) => ({
          ...prev,
          center_ids: prev.center_ids?.filter((id) =>
            attachedCenterIds.includes(id)
          ),
        }));
      }
    } else {
      // Reset to all centers if no fee config is found
      setFilteredCenterOptions(centerOptions);

      // If no course selected yet, make sure "Apply to All Centers" is unchecked
      if (!formData.course_id && formData.all_centers) {
        setFormData((prev) => ({
          ...prev,
          all_centers: false,
        }));
      }
    }
  }, [formData.course_id, courseFeeConfig, centers, centerOptions]);

  // Form change handlers
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;

    if (type === "checkbox") {
      const { checked } = e.target as HTMLInputElement;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAllCentersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      all_centers: checked,
      center_ids: checked ? [] : prev.center_ids,
    }));

    if (checked) {
      setSelectedCenters([]);
    }
  };

  const handleCenterChange = (selected: Option[] | null) => {
    if (selected) {
      setSelectedCenters(selected);
      const centerIds = selected.map((item) => parseInt(item.value, 10));
      setFormData((prev) => ({ ...prev, center_ids: centerIds }));
    } else {
      setSelectedCenters([]);
      setFormData((prev) => ({ ...prev, center_ids: [] }));
    }
  };

  const handleSelectChange = (fieldName: string, selected: any) => {
    if (selected) {
      setFormData((prev) => ({
        ...prev,
        [fieldName]:
          fieldName === "share_type"
            ? selected.value
            : parseInt(selected.value, 10),
      }));

      // If share type changes, we may need to update the course stage values
      if (fieldName === "share_type") {
        // Reset all stage share amounts if share type changes
        const newStages = [...courseStages].map((stage) => ({
          ...stage,
          shareAmount: 0,
        }));
        setCourseStages(newStages);
      }
    }
  };

  // Handle stage share amount change
  const handleStageShareAmountChange = (index: number, value: string) => {
    const parsedValue = parseFloat(value) || 0;
    let finalValue = parsedValue;
    const stageAmount = courseStages[index].amount;

    // If share type is percent, restrict to 0-100 range
    if (formData.share_type === "percent") {
      if (finalValue > 100) finalValue = 100;
      if (finalValue < 0) finalValue = 0;
    } else if (formData.share_type === "fixed") {
      if (finalValue > stageAmount) finalValue = stageAmount;
      if (finalValue < 0) finalValue = 0;
    }

    const newStages = [...courseStages];
    newStages[index].shareAmount = finalValue;
    setCourseStages(newStages);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.course_id) {
      showToast({
        title: "Error",
        text: "Please select a course",
        icon: "error",
      });
      return;
    }

    if (!formData.share_type) {
      showToast({
        title: "Error",
        text: "Please select a share type",
        icon: "error",
      });
      return;
    }

    // Validate that at least one stage has a share amount
    const hasStageWithShareAmount = courseStages.some(
      (stage) => stage.shareAmount > 0
    );
    if (!hasStageWithShareAmount && courseStages.length > 0) {
      showToast({
        title: "Error",
        text: "Please enter at least one stage share amount",
        icon: "error",
      });
      return;
    }

    if (!formData.all_centers && formData.center_ids?.length === 0) {
      showToast({
        title: "Error",
        text: "Please select at least one center or apply to all centers",
        icon: "error",
      });
      return;
    }

    try {
      // Create payload from form data
      const payload = {
        course_id: formData.course_id,
        type_id: formData.type_id,
        share_type: formData.share_type,
        all_centers: formData.all_centers,
        center_ids: formData.center_ids,
        stageFeeShare: courseStages.map((stage) => ({
          id: stage.id,
          stage_id: stage.stage_id,
          share_amount: stage.shareAmount,
        })),
      };

      let result;
      if (isEdit && id) {
        // Update existing fee share
        result = await updateFeeShare(id, payload);
      } else {
        // Add new fee share
        result = await addFeeShare(payload);
      }

      if (result.success) {
        showToast({
          title: "Success",
          text: isEdit
            ? "Course fee share updated successfully"
            : "Course fee share created successfully",
          icon: "success",
        });
        // navigate(routes.courseFeeShareList);
        navigate(-1);
      }
    } catch (error: any) {
      showToast({
        title: "Error",
        text:
          error.message ||
          `An error occurred while ${isEdit ? "updating" : "creating"
          } fee share`,
        icon: "error",
      });
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleCourseChange = (option: Option | null) => {
    const courseValue = option?.value || "";
    setSelectedCourse(courseValue);
    // No need to reset dependent fields here since handleSelectChange will be called too
  };

  // Determine if the component is still loading data
  const isLoading = isEdit && isLoadingFeeShare;

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">
                {isEdit ? "Edit" : "Add"} Course Fee Share
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.courseFeeShareList}>
                      Course Fee Shares
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit" : "Add"} Course Fee Share
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          {isLoading ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading course fee share data...</p>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-md-12">
                <form onSubmit={handleSubmit}>
                  {/* Fee Share Information Section */}
                  <div className="card mb-3">
                    <div className="card-header bg-light">
                      <div className="d-flex align-items-center">
                        <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                          <i className="ti ti-info-square-rounded fs-16" />
                        </span>
                        <h4 className="text-dark">Fee Share Information</h4>
                      </div>
                    </div>
                    <div className="card-body pb-1">
                      <div className="row">

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Course <span className="text-danger">*</span>
                            </label>
                            <CommonSelect
                              className="select"
                              options={courseOptions}
                              value={courseOptions.find(
                                (option) =>
                                  option.value === formData.course_id.toString()
                              )}
                              onChange={(selected) => {
                                handleCourseChange(selected);
                                handleSelectChange("course_id", selected);
                              }}
                              placeholder={
                                isLoadingCourses
                                  ? "Loading courses..."
                                  : "Select Course"
                              }
                              required
                            />
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Fee Type <span className="text-danger">*</span>
                            </label>
                            <CommonSelect
                              className="select"
                              value={feeTypeOption || undefined}
                              options={feeTypeOption ? [feeTypeOption] : []}
                              isDisabled={true}
                              placeholder="Course Fee"
                            />
                          </div>
                        </div>

                        {/* Share Type field - Always show */}
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Share Type <span className="text-danger">*</span>
                            </label>
                            <CommonSelect
                              className="select"
                              options={shareTypeOptions}
                              value={shareTypeOptions.find(
                                (option) => option.value === formData.share_type
                              )}
                              onChange={(selected) =>
                                handleSelectChange("share_type", selected)
                              }
                              placeholder="Select Share Type"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Show Course Stage Fees section */}
                  {courseStages.length > 0 && (
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <div className="d-flex align-items-center">
                          <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                            <i className="ti ti-list-details fs-16" />
                          </span>
                          <h4 className="text-dark">Course Level Fees</h4>
                        </div>
                      </div>
                      <div className="card-body pb-1">
                        {isLoadingCourseFeeConfig ? (
                          <div className="text-center py-3">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="mt-2">Loading fee configuration...</p>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-bordered">
                              <thead className="table-light">
                                <tr>
                                  <th>Level</th>
                                  <th>Fee Amount</th>
                                  <th>
                                    Share Amount (
                                    {formData.share_type === "fixed"
                                      ? "₹"
                                      : "%"}
                                    )
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {courseStages.map((stage, index) => (
                                  <tr key={stage.stage_id}>
                                    <td>{stage.name}</td>
                                    <td>₹{stage.amount.toLocaleString()}</td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder={`Enter share amount ${formData.share_type === "fixed"
                                            ? "(₹)"
                                            : "(%)"
                                          }`}
                                        value={stage.shareAmount}
                                        onChange={(e) =>
                                          handleStageShareAmountChange(
                                            index,
                                            e.target.value
                                          )
                                        }
                                        min="0"
                                        max={
                                          formData.share_type === "percent"
                                            ? 100
                                            : stage.amount
                                        }
                                        step={
                                          formData.share_type === "percent"
                                            ? "1"
                                            : "1"
                                        }
                                      />
                                      {formData.share_type === "percent" ? (
                                        <small className="text-muted">
                                          Max: 100%
                                        </small>
                                      ) : (
                                        <small className="text-muted">
                                          Max: ₹{stage.amount.toLocaleString()}
                                        </small>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recipients Section */}
                  <div className="card mb-3">
                    <div className="card-header bg-light">
                      <div className="d-flex align-items-center">
                        <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                          <i className="ti ti-users fs-16" />
                        </span>
                        <h4 className="text-dark">Center Selection</h4>
                      </div>
                    </div>
                    <div className="card-body pb-1">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Centers</label>
                            <div className="form-check mb-2">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="allCentersCheck"
                                name="all_centers"
                                checked={formData.all_centers}
                                onChange={handleAllCentersChange}
                                disabled={filteredCenterOptions.length === 0}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="allCentersCheck"
                                style={{
                                  opacity:
                                    filteredCenterOptions.length === 0
                                      ? 0.6
                                      : 1,
                                }}
                              >
                                Apply to All Centers
                                {filteredCenterOptions.length === 0 && (
                                  <span className="text-muted ms-2">
                                    (No centers available)
                                  </span>
                                )}
                              </label>
                            </div>

                            {!formData.all_centers && (
                              <div>
                                <MultiSelect
                                  className="select"
                                  options={filteredCenterOptions}
                                  isMulti={true}
                                  value={selectedCenters}
                                  onChange={handleCenterChange}
                                  placeholder={
                                    isLoadingCenters
                                      ? "Loading centers..."
                                      : filteredCenterOptions.length === 0
                                        ? "No eligible centers found"
                                        : "Select centers..."
                                  }
                                />
                                {filteredCenterOptions.length === 0 && (
                                  <small className="text-warning d-block mt-1">
                                    No centers have this fee configuration
                                    attached.
                                  </small>
                                )}
                                {selectedCenters.length === 0 &&
                                  filteredCenterOptions.length > 0 && (
                                    <small className="text-muted d-block mt-1">
                                      No centers selected
                                    </small>
                                  )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="text-end mb-3">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isAdding || isUpdating}
                    >
                      {isAdding ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                          ></span>
                          Saving...
                        </>
                      ) : isUpdating ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                          ></span>
                          Updating...
                        </>
                      ) : isEdit ? (
                        "Update Course Fee Share"
                      ) : (
                        "Save Course Fee Share"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AddCourseFeeShare;
